import { Link } from 'react-router'
import { REGIONS } from '#app/data/regions.ts'
import { prisma } from '#app/utils/db.server.ts'
import { getDomainUrl } from '#app/utils/misc.tsx'
import { type Route } from './+types/index.ts'

export const meta: Route.MetaFunction = () => {
	const year = new Date().getFullYear()
	return [
		{ title: `Good to Order (${year}) | Good Food Nearby + What to Order` },
		{
			name: 'description',
			content:
				'Find good food nearby in London fast, then see what to order first. Handpicked spots, not just expensive restaurants.',
		},
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const canonicalOrigin = getDomainUrl(request)
	// Top scoring restaurants for the intelligence ticker
	const topRestaurants = await prisma.restaurant.findMany({
		where: { status: 'published', consensusScore: { gt: 0 } },
		orderBy: { consensusScore: 'desc' },
		take: 4,
		include: {
			tags: { include: { tag: true } },
			picks: { where: { type: 'safe_first' }, take: 1 },
		},
	})

	// Divergence: Low-scoring restaurant with decent Google rating (the "hype")
	const lowScoreRestaurant = await prisma.restaurant.findFirst({
		where: {
			status: 'published',
			consensusScore: { gte: 25, lte: 45 },
			googleRating: { gte: 4.0 },
		},
		orderBy: { consensusScore: 'asc' },
		include: {
			tags: { include: { tag: true } },
			picks: { where: { type: 'safe_first' }, take: 1 },
		},
	})

	// Divergence: High-scoring hidden gem
	const highScoreRestaurant = await prisma.restaurant.findFirst({
		where: {
			status: 'published',
			consensusScore: { gte: 50 },
		},
		orderBy: { consensusScore: 'desc' },
		include: {
			tags: { include: { tag: true } },
			picks: { where: { type: 'safe_first' }, take: 1 },
		},
	})

	// Published restaurant counts per area tag (for region cards)
	const areaTags = await prisma.tag.findMany({
		where: { citySlug: 'london', type: 'area' },
		include: {
			restaurants: {
				where: { restaurant: { status: 'published' } },
			},
		},
	})

	const areaCounts = Object.fromEntries(
		areaTags.map((tag) => [tag.slug, tag.restaurants.length]),
	)

	// Total restaurant count
	const totalRestaurants = Object.values(areaCounts).reduce((a, b) => a + b, 0)

	return {
		canonicalOrigin,
		topRestaurants: topRestaurants.map((r) => ({
			name: r.name,
			slug: r.slug,
			area: r.tags.find((rt) => rt.tag.type === 'area')?.tag.name ?? 'London',
			score: r.consensusScore ?? 0,
			safeFirstOrder: r.picks[0]?.title ?? null,
		})),
		areaCounts,
		totalRestaurants,
		divergence: {
			lowScore: lowScoreRestaurant
				? {
						name: lowScoreRestaurant.name,
						slug: lowScoreRestaurant.slug,
						consensusScore: lowScoreRestaurant.consensusScore,
						googleRating: lowScoreRestaurant.googleRating,
						cuisine: lowScoreRestaurant.cuisine,
					}
				: null,
			highScore: highScoreRestaurant
				? {
						name: highScoreRestaurant.name,
						slug: highScoreRestaurant.slug,
						consensusScore: highScoreRestaurant.consensusScore,
						googleRating: highScoreRestaurant.googleRating,
						cuisine: highScoreRestaurant.cuisine,
						isHiddenGem: highScoreRestaurant.isHiddenGem,
						safeFirstOrder: highScoreRestaurant.picks[0]?.title ?? null,
					}
				: null,
		},
		primaryRegion: {
			name: 'Soho',
			slug: 'soho',
			region: 'central',
			count: areaCounts['soho'] ?? 0,
		},
	}
}

export default function Index({ loaderData }: Route.ComponentProps) {
	const {
		canonicalOrigin,
		topRestaurants,
		areaCounts,
		totalRestaurants,
		divergence,
	} = loaderData

	const ldWebSite = {
		'@context': 'https://schema.org',
		'@type': 'WebSite',
		name: 'Good to Order',
		url: canonicalOrigin,
		inLanguage: 'en-GB',
		description: 'Find good food nearby in London and see what to order first.',
	}

	return (
		<main className="min-h-screen bg-[#05050a] text-[#f8f8ff]">
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(ldWebSite) }}
			/>
			{/* Hero Section - Outcome Focused */}
			<section className="relative border-b border-white/10">
				<div className="absolute inset-0 bg-gradient-to-br from-[#0a0a14] via-[#05050a] to-[#0a001f]" />
				<div className="relative container mx-auto px-6 py-16 lg:py-24">
					<div className="flex max-w-5xl flex-col gap-6">
						<span className="font-mono text-[11px] tracking-[0.3em] text-gray-400 uppercase">
							{totalRestaurants}+ London spots
						</span>
						<h1 className="text-4xl leading-[0.95] font-black tracking-tighter text-[#f8f8ff] sm:text-5xl lg:text-6xl">
							Find somewhere good nearby.
							<br />
							<span className="text-[#00e0ff]">Know what to order.</span>
						</h1>
						<p className="max-w-3xl text-lg leading-relaxed text-gray-300 sm:text-xl">
							Local spots people actually recommend, organised by area. Not just
							the famous places. Plus{' '}
							<span className="font-semibold text-[#f8f8ff]">
								the dishes worth ordering
							</span>
							.
						</p>
						<p className="text-sm text-gray-500">
							Menus change. These are the dishes people loved. Worth asking
							about.
						</p>
						<div className="flex flex-wrap gap-3 pt-2">
							<Link
								to="/london/near-me"
								className="btn btn-primary gap-2 border-[#422ad5] bg-[#422ad5] px-6 py-3 text-base font-bold tracking-[0.1em] text-white uppercase hover:bg-[#5b3bff]"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth={2}
									stroke="currentColor"
									className="size-5"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
									/>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
									/>
								</svg>
								Explore Near Me
							</Link>
							<Link
								to="/london"
								className="btn btn-outline border-white/30 text-[#f8f8ff] hover:border-[#00e0ff] hover:bg-transparent hover:text-[#00e0ff]"
							>
								Browse London
							</Link>
						</div>
					</div>
				</div>
			</section>

			{/* What to Order Ticker - Shows the actual value */}
			<section className="border-b border-white/10 bg-[#0b0b12]">
				<div className="container mx-auto px-6 py-5">
					<div className="mb-3 flex items-center gap-4 font-mono text-xs tracking-[0.2em] text-gray-400 uppercase">
						<span className="text-[#00e0ff]">What people are ordering</span>
						<div className="h-px flex-1 bg-white/10" />
					</div>
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
						{topRestaurants.map((r) => (
							<Link
								key={r.slug}
								to={`/london/restaurants/${r.slug}`}
								className="group rounded-lg border border-transparent bg-white/5 p-4 transition-colors hover:border-[#422ad5]/50 hover:bg-white/10"
							>
								<div className="mb-2 flex items-start justify-between gap-2">
									<span className="font-bold tracking-tight text-[#f8f8ff] transition-colors group-hover:text-[#00e0ff]">
										{r.name}
									</span>
									<span className="shrink-0 text-sm font-bold text-[#00e0ff]">
										{Math.round(r.score)}
									</span>
								</div>
								{r.safeFirstOrder && (
									<div className="text-sm">
										<span className="text-gray-500">Order: </span>
										<span className="text-[#f8f8ff]">{r.safeFirstOrder}</span>
									</div>
								)}
								<span className="mt-2 block text-[10px] tracking-[0.15em] text-gray-500 uppercase">
									{r.area}
								</span>
							</Link>
						))}
					</div>
				</div>
			</section>

			{/* The Problem - Why You Need This */}
			{divergence.lowScore && divergence.highScore && (
				<section className="border-b border-white/10 bg-[#0a0a12]">
					<div className="container mx-auto px-6 py-12 lg:py-16">
						<div className="mb-10 text-center">
							<span className="font-mono text-[11px] tracking-[0.3em] text-[#ff5c8a] uppercase">
								The Problem
							</span>
							<h2 className="mt-3 text-2xl font-black tracking-tight text-[#f8f8ff] sm:text-3xl">
								Google shows you everything. We show you what's good.
							</h2>
							<p className="mx-auto mt-2 max-w-2xl text-gray-400">
								Hidden gems, local favourites, the spots people quietly rave
								about. Plus what to order when you get there.
							</p>
						</div>

						<div className="mx-auto grid max-w-4xl gap-6 md:grid-cols-2">
							{/* LOW SCORE - Tourist Trap Example */}
							<div className="card relative overflow-hidden border border-[#1a1a2e] bg-[#0d0d15] shadow-[0_0_20px_rgba(66,42,213,0.12)]">
								<div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-[#ff2e63] to-[#ff5c8a]" />
								<div className="card-body space-y-4 p-6">
									<div className="flex items-start justify-between">
										<div>
											<span className="font-mono text-[10px] tracking-[0.2em] text-[#ff5c8a] uppercase">
												Less buzz
											</span>
											<h3 className="mt-1 text-xl font-bold text-[#f8f8ff]">
												{divergence.lowScore.name}
											</h3>
											<p className="text-xs tracking-[0.1em] text-gray-400 uppercase">
												{divergence.lowScore.cuisine}
											</p>
										</div>
										<div className="flex flex-col items-center">
											<span className="text-3xl font-black text-[#ff5c8a]">
												{divergence.lowScore.consensusScore}
											</span>
											<span className="text-[10px] tracking-wider text-gray-500 uppercase">
												Score
											</span>
										</div>
									</div>

									<div className="flex items-center gap-2 text-sm">
										<span className="text-[#f8f8ff]">Google:</span>
										<span className="font-semibold text-[#00e0ff]">
											{divergence.lowScore.googleRating?.toFixed(1)}★
										</span>
										<span className="text-gray-500">looks great, right?</span>
									</div>

									<div className="rounded-lg border-l-2 border-[#ff5c8a] bg-[#151525] p-3">
										<p className="text-sm text-gray-400 italic">
											Fewer people talking about what to order here.
										</p>
									</div>
								</div>
							</div>

							{/* HIGH SCORE - Hidden Gem Example */}
							<div className="card relative overflow-hidden border border-[#1a1a2e] bg-[#0d0d15] shadow-[0_0_20px_rgba(0,224,255,0.15)]">
								<div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-[#00e0ff] to-[#422ad5]" />
								<div className="card-body space-y-4 p-6">
									<div className="flex items-start justify-between">
										<div>
											<span className="font-mono text-[10px] tracking-[0.2em] text-[#00e0ff] uppercase">
												More buzz
											</span>
											<h3 className="mt-1 text-xl font-bold text-[#f8f8ff]">
												{divergence.highScore.name}
											</h3>
											<p className="text-xs tracking-[0.1em] text-gray-400 uppercase">
												{divergence.highScore.cuisine}
											</p>
										</div>
										<div className="flex flex-col items-center">
											<span className="text-3xl font-black text-[#00e0ff]">
												{divergence.highScore.consensusScore}
											</span>
											<span className="text-[10px] tracking-wider text-gray-500 uppercase">
												Score
											</span>
										</div>
									</div>

									<div className="flex flex-wrap gap-2">
										{divergence.highScore.isHiddenGem && (
											<span className="badge border border-[#00e0ff] text-[10px] tracking-[0.1em] text-[#00e0ff] uppercase">
												Hidden Gem
											</span>
										)}
										<span className="badge border border-[#422ad5] text-[10px] tracking-[0.1em] text-[#422ad5] uppercase">
											Solid Bet
										</span>
									</div>

									{divergence.highScore.safeFirstOrder && (
										<div className="rounded-lg border-l-2 border-[#00e0ff] bg-[#151525] p-3">
											<span className="text-[10px] tracking-wider text-[#00e0ff] uppercase">
												What to Order
											</span>
											<p className="mt-1 text-sm font-semibold text-[#f8f8ff]">
												{divergence.highScore.safeFirstOrder}
											</p>
										</div>
									)}

									<Link
										to={`/london/restaurants/${divergence.highScore.slug}`}
										className="btn btn-sm btn-outline w-full border-[#00e0ff] text-[#00e0ff] hover:bg-[#00e0ff] hover:text-[#05050a]"
									>
										See the full breakdown →
									</Link>
								</div>
							</div>
						</div>
					</div>
				</section>
			)}

			{/* How It Works - Simplified */}
			<section className="border-b border-white/10">
				<div className="container mx-auto px-6 py-12">
					<div className="mx-auto max-w-4xl">
						<div className="mb-8 text-center">
							<h2 className="text-2xl font-black tracking-tight text-[#f8f8ff]">
								How It Works
							</h2>
						</div>

						<div className="grid gap-6 sm:grid-cols-3">
							<div className="text-center">
								<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-[#422ad5] bg-[#422ad520]">
									<span className="text-lg font-black text-[#422ad5]">1</span>
								</div>
								<h3 className="mb-2 font-bold text-[#f8f8ff]">Pick an area</h3>
								<p className="text-sm text-gray-400">
									Peckham, Soho, Dalston, wherever. See what's good nearby.
								</p>
							</div>

							<div className="text-center">
								<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-[#00e0ff] bg-[#00e0ff20]">
									<span className="text-lg font-black text-[#00e0ff]">2</span>
								</div>
								<h3 className="mb-2 font-bold text-[#f8f8ff]">Find a spot</h3>
								<p className="text-sm text-gray-400">
									Hidden gems and local favourites, not just the obvious places.
								</p>
							</div>

							<div className="text-center">
								<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-[#ff9f1c] bg-[#ff9f1c20]">
									<span className="text-lg font-black text-[#ff9f1c]">3</span>
								</div>
								<h3 className="mb-2 font-bold text-[#f8f8ff]">
									Know what to order
								</h3>
								<p className="text-sm text-gray-400">
									The dishes people actually recommend. Walk in ready.
								</p>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Regional Compass */}
			<section id="compass" className="container mx-auto px-0 py-12 sm:px-6">
				<div className="mb-8 flex items-center justify-between gap-4 px-6 sm:px-0">
					<div>
						<p className="font-mono text-[11px] tracking-[0.3em] text-gray-500 uppercase">
							Explore London
						</p>
						<h2 className="mt-2 text-3xl font-black tracking-tight text-[#f8f8ff] sm:text-4xl">
							Pick a region
						</h2>
					</div>
					<Link
						to="/london"
						className="btn btn-link btn-sm hidden p-0 text-[#00e0ff] sm:inline-flex"
					>
						View all neighbourhoods →
					</Link>
				</div>

				{/* Featured Region - Central (Soho) */}
				{(() => {
					const centralRegion = REGIONS.find((r) => r.slug === 'central')!
					const centralCount = centralRegion.areaSlugs.reduce(
						(total, slug) => total + (areaCounts[slug] ?? 0),
						0,
					)
					return (
						<Link
							to="/london/central"
							className="group relative mx-6 mb-6 block h-[50vh] overflow-hidden rounded-xl border border-[#1a1a2e] shadow-[0_0_40px_rgba(0,224,255,0.15)] sm:mx-0 lg:h-[60vh]"
						>
							<div className="absolute inset-0 z-0">
								<img
									src={centralRegion.img}
									alt={centralRegion.name}
									className="h-full w-full object-cover brightness-[0.6] grayscale-[30%] transition-all duration-1000 group-hover:scale-105 group-hover:grayscale-0"
								/>
								<div className="absolute inset-0 bg-gradient-to-t from-[#05050a] via-[#05050a]/60 to-transparent" />
							</div>

							<div className="relative z-10 flex h-full flex-col justify-between p-8 lg:p-12">
								<div className="flex items-center gap-3">
									<span className="rounded-full border border-[#00e0ff] bg-[#00e0ff10] px-4 py-1.5 text-[11px] tracking-[0.2em] text-[#00e0ff] uppercase">
										Start Here
									</span>
									<span className="font-mono text-[10px] tracking-[0.3em] text-gray-400 uppercase">
										{centralRegion.vibe}
									</span>
								</div>

								<div className="max-w-2xl">
									<h3 className="text-5xl font-black tracking-tighter text-[#f8f8ff] uppercase italic transition-colors group-hover:text-[#00e0ff] lg:text-7xl">
										{centralRegion.name}
									</h3>
									<p className="mt-3 text-base text-gray-300 lg:text-lg">
										{centralRegion.areas}
									</p>
									<div className="mt-6 flex items-center gap-4">
										<span className="rounded-full border-2 border-[#00e0ff] bg-[#00e0ff15] px-5 py-2 text-lg font-bold tracking-[0.1em] text-[#00e0ff] uppercase">
											{centralCount} Restaurants
										</span>
										<span className="font-semibold text-[#f8f8ff] transition-colors group-hover:text-[#00e0ff]">
											See what to order →
										</span>
									</div>
								</div>
							</div>
						</Link>
					)
				})()}

				{/* Other Regions Grid */}
				<div className="grid grid-cols-2 gap-4 px-6 sm:px-0 lg:grid-cols-4">
					{REGIONS.filter((r) => r.slug !== 'central').map((region) => {
						const areaCount = region.areaSlugs.reduce(
							(total, slug) => total + (areaCounts[slug] ?? 0),
							0,
						)

						return (
							<Link
								key={region.slug}
								to={`/london/${region.slug}`}
								className="group relative h-[30vh] overflow-hidden rounded-lg border border-[#1a1a2e] hover:border-[#422ad5]"
							>
								<div className="absolute inset-0 z-0">
									<img
										src={region.img}
										alt={region.name}
										className="h-full w-full object-cover brightness-50 grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
									/>
									<div className="absolute inset-0 bg-gradient-to-t from-[#05050a] via-transparent to-transparent" />
								</div>

								<div className="relative z-10 flex h-full flex-col justify-end p-5">
									<span className="mb-1 font-mono text-[9px] tracking-[0.25em] text-gray-500 uppercase">
										{region.vibe}
									</span>
									<h3 className="text-2xl font-black tracking-tighter text-[#f8f8ff] uppercase italic transition-colors group-hover:text-[#ff5c8a]">
										{region.name}
									</h3>
									<span className="mt-2 inline-block text-[11px] tracking-[0.15em] text-[#00e0ff] uppercase">
										{areaCount > 0 ? `${areaCount} spots` : 'Explore areas'}
									</span>
								</div>
							</Link>
						)
					})}
				</div>
			</section>

		</main>
	)
}
